import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePagination } from '../hooks/usePagination';

const Pagination = ({
    data = [],
    pageSize,
    currentPage: externalCurrentPage,
    setCurrentPage: externalSetCurrentPage,
    totalCount,
    setPaginatedData,
}) => {
    const { currentPage, totalPages, setCurrentPage, paginatedData } = usePagination({
        data,
        itemsPerPage: pageSize,
        setPaginatedData,
    });

    const isClientSide = !externalCurrentPage && !externalSetCurrentPage && !totalCount;
    const finalCurrentPage = isClientSide ? currentPage : externalCurrentPage;
    const finalSetCurrentPage = isClientSide ? setCurrentPage : externalSetCurrentPage;
    const finalTotalPages = isClientSide
        ? totalPages
        : useMemo(() => {
              if (!totalCount || !pageSize) return 0;
              return Math.ceil(totalCount / pageSize);
          }, [totalCount, pageSize]);

    useEffect(() => {
        if (finalCurrentPage > finalTotalPages && finalCurrentPage !== 1 && finalTotalPages > 0) {
            finalSetCurrentPage(1);
        }
    }, [data, finalTotalPages, finalCurrentPage, finalSetCurrentPage]);

    const pageNumbers = useMemo(() => {
        const pages = [];
        if (finalTotalPages <= 7) {
            for (let i = 1; i <= finalTotalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (finalCurrentPage > 3) pages.push('...');
            const start = Math.max(2, finalCurrentPage - 1);
            const end = Math.min(finalTotalPages - 1, finalCurrentPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (finalCurrentPage < finalTotalPages - 2) pages.push('...');
            pages.push(finalTotalPages);
        }
        return pages;
    }, [finalCurrentPage, finalTotalPages]);

    const handleClick = page => {
        if (page !== '...' && page !== finalCurrentPage) {
            finalSetCurrentPage(page);
        }
    };

    const imageBaseUrl = useSelector(state => state.config.image_base_url);

    const showNavButtons = isClientSide ? paginatedData?.length > 0 : data?.length > 0;
    if (!showNavButtons) return null;

    return (
        <div className="flex items-center space-x-2">
            {showNavButtons && (
                <button
                    type="button"
                    onClick={() => handleClick(finalCurrentPage - 1)}
                    disabled={finalCurrentPage === 1}
                    aria-label="Previous page"
                    className="px-3 py-1 rounded !bg-secondary disabled:opacity-50 w-9 h-9 flex items-center justify-center">
                    <img
                        src={`${imageBaseUrl}/junoclient/left-arrow-icon.svg`}
                        alt="Previous page"
                        className="w-4 h-4 invert-0 dark:invert"
                    />
                </button>
            )}

            {pageNumbers.map((number, idx) =>
                number === '...' ? (
                    <span
                        key={`dots-${idx}`}
                        className="pb-1 !text-primary !bg-secondary w-9 h-9 flex justify-center items-end">
                        ...
                    </span>
                ) : (
                    <button
                        type="button"
                        key={`page-${number}`}
                        onClick={() => handleClick(number)}
                        aria-current={number === finalCurrentPage ? 'page' : undefined}
                        className={`w-9 h-9 rounded font-medium font-3 ${
                            number === finalCurrentPage
                                ? '!bg-[#1A8A7E] text-[#FFFFFF]'
                                : '!bg-secondary !text-primary'
                        }`}>
                        {number}
                    </button>
                ),
            )}

            {showNavButtons && (
                <button
                    onClick={() => handleClick(finalCurrentPage + 1)}
                    type="button"
                    aria-label="Next page"
                    disabled={finalCurrentPage === finalTotalPages}
                    className="px-3 py-1 rounded !bg-secondary disabled:opacity-50 w-9 h-9 flex items-center justify-center">
                    <img
                        src={`${imageBaseUrl}/junoclient/right-arrow-icon.svg`}
                        alt="Next page"
                        className="w-4 h-4 invert-0 dark:invert"
                    />
                </button>
            )}
        </div>
    );
};

export default Pagination;
